/**
 * Карточка велика (§2.2) — единственный экран про один велосипед.
 *
 * Один маршрут для всех входов (сканер, список, карта, задания): раньше их
 * было два (`/task/:id` и `/vehicle/:id`), потому что команды замку
 * гейтились взятой задачей. Гейт по роли (см. `api/device.ts`) снял эту
 * причину, и вторая цель осталась не нужна — сканер теперь тоже всегда
 * ведёт сюда (`lib/scanner/resolve.ts`).
 *
 * Порядок блоков — порядок §2.2: шапка → замок → что с ним делать → найти →
 * задание (если есть) → история (свёрнута) → замечание диспетчеру.
 */
import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useLastDrop,
  useRideZones,
  useScoutVehicles,
  displayNumber,
} from '@/api/vehicles'
import { useLocateTracking, useScoutDeviceRefresh, useVehicleTrail } from '@/api/device'
import {
  completionFor,
  useClaimTask,
  useCompleteTask,
  useCreatePickupTask,
  useCreateRebalanceTask,
  useMyTasks,
  useTaskPool,
  useVehiclesWithTask,
} from '@/api/scout'
import { useHubs } from '@/api/hubs'
import { useMyPool } from '@/api/pool'
import { useAuth } from '@/lib/auth'
import { useOnline } from '@/lib/use-online'
import { applyDeviceReading, mergeBikeView, taskForVehicle, type BikeView } from '@/lib/bike-view'
import { describeBike, pickupNoteFor } from '@/lib/describe-bike'
import { distanceM, hasPosition, timeAgo, useMyPosition } from '@/lib/geo'
import { pushRecentBike } from '@/lib/recent-bikes'
import { queuedTargets } from '@/lib/offline/outbox'
import { toggle as togglePickup, usePickupSelection } from '@/lib/pickup-selection'
import { useOutboxEntries } from '@/lib/offline/use-outbox-entries'
import { effectivePoolState, poolPanelFor, poolQueue } from '@/lib/pool'
import { heldByCollectorLabel } from '@/lib/vehicle-status'
import { BigButton, Notice } from '@/components/ui'
import { BikeHeader } from '@/components/bike/bike-header'
import { BikeLabels } from '@/components/bike/bike-labels'
import { BikeLocate } from '@/components/bike/bike-locate'
import { BikeLock } from '@/components/bike/bike-lock'
import { BikeActions } from '@/components/bike/bike-actions'
import { BikeFind } from '@/components/bike/bike-find'
import { BikeTask } from '@/components/bike/bike-task'
import { ReportSheet } from '@/components/bike/report-sheet'
import { PoolPanel } from '@/components/bike/pool-panel'

const MiniMap = lazy(() => import('@/components/bike/mini-map'))

export default function BikeCardPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const online = useOnline()
  const { userId, isPartner, roles } = useAuth()
  const me = useMyPosition()

  const vehicles = useScoutVehicles()
  const mine = useMyTasks()
  const pool = useTaskPool()
  const zones = useRideZones()
  const hubs = useHubs()
  // Партнёру пул не откроют (403) — и спрашивать незачем.
  const myPool = useMyPool({ enabled: !isPartner })

  const claim = useClaimTask()
  const complete = useCompleteTask()
  const createPickup = useCreatePickupTask()
  const createRebalance = useCreateRebalanceTask()
  const withTask = useVehiclesWithTask()

  const [reportOpen, setReportOpen] = useState(false)

  // Отметка «Забираю» — общая с картой и списком.
  const pickedIds = usePickupSelection()

  /*
   * ⚠ «ЗАДАЧУ СОЗДАЛИ» — ЭТО ВОПРОС К ОЧЕРЕДИ, А НЕ СОБСТВЕННАЯ ПАМЯТЬ ЭКРАНА.
   *
   * Заведение задачи двухступенчатое: нажатие кладёт запись в очередь, а
   * приговор выносит сервер минутами позже, из другого места программы
   * (`drainOutbox` в `app/providers.tsx`). Отклонённая запись исчезает из
   * очереди без сообщения экрану — единственный способ узнать об этом
   * (и не дать нажать дважды без сети) — спрашивать очередь напрямую.
   */
  // `?? []` — этому экрану разница между «очередь пуста» и «ещё не
  // прочитана» безразлична: оба состояния значат «заведённого в очереди
  // не вижу», а через кадр придёт правда.
  const queue = useOutboxEntries() ?? []

  const bike = useMemo<BikeView | null>(() => {
    const tasks = [...(mine.data?.tasks ?? []), ...(pool.data?.tasks ?? [])]
    const task = taskForVehicle(id, tasks)
    const vehicle = (vehicles.data?.vehicles ?? []).find((v) => v.id === id)
    return mergeBikeView(vehicle, task)
  }, [vehicles.data, mine.data, pool.data, id])

  const lastDrop = useLastDrop(id, online && !isPartner)
  const trail = useVehicleTrail(id, online)
  const gpsRefresh = useScoutDeviceRefresh(id)
  const locate = useLocateTracking(id)

  // Открытая карточка — это «недавний» с главного экрана, даже если сюда
  // пришли не через ввод номера (скан, список, карта, задание).
  //
  // ⚠ Зависимость — ИМЕННО `vehicleId`, а не весь `bike`. Последний — новый
  // объект на каждый рефетч парка (карта держит его раз в 20 с, пока
  // открыта), и запись в localStorage на каждый такой тик — лишняя работа
  // без пользы: номер байка между этими рефетчами не меняется.
  const bikeVehicleId = bike?.vehicleId ?? ''
  useEffect(() => {
    if (bike && bikeVehicleId) pushRecentBike({ id: bikeVehicleId, number: displayNumber(bike) })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- см. комментарий выше
  }, [bikeVehicleId])

  const loading = !bike && (vehicles.isLoading || mine.isLoading || pool.isLoading)

  if (loading) {
    return (
      <main className="mx-auto w-full max-w-lg p-4">
        <p className="opacity-60">Загружаем…</p>
      </main>
    )
  }

  if (!bike) {
    return (
      <main className="mx-auto w-full max-w-lg p-4 pb-tabs">
        <Notice tone="warn">Байк не найден. Возможно, он выбыл из парка.</Notice>
        <button className="mt-4 min-h-11 underline opacity-70" onClick={() => void navigate('/')}>
          К поиску по номеру
        </button>
      </main>
    )
  }

  // «Обновить GPS» (§2.2.1): наложить последний успешный опрос устройства
  // поверх снимка из парка/задачи. `bike` остаётся снимком парка как есть —
  // им по-прежнему пользуются `pushRecentBike` выше и кнопки замка/задачи
  // ниже, которым телеметрия не нужна. `readAt` — момент самого опроса
  // (`dataUpdatedAt` запроса), а не время рендера: функция наложения обязана
  // остаться чистой.
  const liveBike = applyDeviceReading(
    bike,
    gpsRefresh.data,
    new Date(gpsRefresh.dataUpdatedAt).toISOString(),
  )

  const status = describeBike(liveBike.vehicle, zones.data)
  const distance = me && hasPosition(liveBike) ? distanceM(me, liveBike) : null
  const rentalBlocked = bike.status === 'in_ride' ? 'Байк в аренде — замок трогать нельзя' : null

  const drop = lastDrop.data
  const droppedPoint = drop?.found ? { lat: drop.lat, lng: drop.lng } : null
  const droppedAgo = drop?.found ? timeAgo(drop.endedAt) : null

  /*
   * Пул водителя. «Мой» — панель сверху (ТЗ: ссылка из Telegram открывает
   * карточку с крупными кнопками пула); «чужой» — только пометка.
   */
  const driverPool = poolPanelFor({
    isPartner,
    userId,
    vehicle: {
      id: bike.vehicleId,
      poolDriverId: bike.vehicle.poolDriverId,
      poolKind: bike.vehicle.poolKind,
    },
    items: myPool.data?.items ?? [],
  })
  const poolState =
    driverPool?.kind === 'mine'
      ? effectivePoolState(driverPool.item, poolQueue(queue))
      : ''
  const inMyPool = driverPool?.kind === 'mine'

  const pickedUp = pickedIds.has(bike.vehicleId)
  const hasAnyTask = bike.task !== null || withTask.has(bike.vehicleId)
  // §2.2.5: невзятая задача диспетчера — «Взять» в шапке, а не в блоке
  // задачи внизу, чтобы не долистывать до неё.
  const openTask = bike.task && bike.task.status === 'open' ? bike.task : null
  /*
   * Заведённое, но ещё не уехавшее. Пока запись здесь — судьба задачи
   * неизвестна, и это единственное состояние, в котором честно обещать
   * «Завершить появится, как только уйдёт на сервер».
   */
  const queuedCreate: 'rebalance' | 'pickup' | null = queuedTargets(
    queue,
    'create_rebalance',
  ).has(bike.vehicleId)
    ? 'rebalance'
    : queuedTargets(queue, 'create_pickup').has(bike.vehicleId)
      ? 'pickup'
      : null

  return (
    <main className="mx-auto w-full max-w-lg p-4 pb-tabs">
      <button className="mb-3 min-h-11 text-sm underline opacity-70" onClick={() => void navigate(-1)}>
        ← Назад
      </button>

      {driverPool?.kind === 'mine' ? (
        <PoolPanel
          item={driverPool.item}
          state={poolState}
          view={liveBike}
          me={me}
          online={online}
        />
      ) : driverPool?.kind === 'other' ? (
        <div className="mb-3">
          <Notice tone="info">В пуле у другого водителя</Notice>
        </div>
      ) : driverPool?.kind === 'collector' ? (
        <div className="mb-3">
          <Notice tone="info">
            {heldByCollectorLabel(liveBike.status, 'collector') ?? 'У сборщика'}
          </Notice>
        </div>
      ) : null}

      <BikeHeader
        view={liveBike}
        status={status}
        distance={distance}
        action={
          openTask ? (
            <BigButton
              tone="primary"
              busy={claim.isPending}
              onClick={() => {
                claim.mutate(openTask.id)
              }}
            >
              Взять
            </BigButton>
          ) : undefined
        }
        gpsRefresh={{
          onRefresh: () => {
            void gpsRefresh.refetch()
          },
          busy: gpsRefresh.isFetching,
          updatedAt: gpsRefresh.dataUpdatedAt,
          error: gpsRefresh.isError ? 'Устройство не ответило' : null,
        }}
      />

      <div className="mb-4">
        <BikeLabels
          vehicleId={bike.vehicleId}
          labels={vehicles.data?.vehicles.find((v) => v.id === id)?.labels ?? []}
          readOnly={isPartner}
        />
      </div>

      <BikeLocate
        vehicleId={bike.vehicleId}
        online={online}
        onStart={locate.start}
        starting={locate.starting}
        error={locate.error}
        active={locate.active}
        secondsLeft={locate.secondsLeft}
        satellites={locate.satellites}
        pointAgoText={locate.pointAgoText}
        onStop={locate.stop}
      />

      {locate.active && hasPosition(liveBike) && online ? (
        <div className="mb-4">
          <Suspense fallback={<div className="h-44 animate-pulse rounded-xl bg-white/5" />}>
            <MiniMap view={liveBike} trail={locate.trail} />
          </Suspense>
        </div>
      ) : null}

      {/*
        Байк из своего пула: замок уже в панели сверху, а ручные статусы
        оставили бы пункт пула висеть (ручной «На базе» у забранного) —
        у водителя для этого «Поставил».
      */}
      {inMyPool ? null : (
        <BikeLock vehicleId={bike.vehicleId} online={online} rentalBlocked={rentalBlocked} />
      )}

      {isPartner || inMyPool ? null : (
        <BikeActions vehicleId={bike.vehicleId} status={bike.status} />
      )}

      <BikeFind
        vehicleId={bike.vehicleId}
        online={online}
        showHeadlight={!isPartner}
        showSiren={!inMyPool}
      />

      {/*
        «Забираю» — отметка в маршруте объезда, а не действие над байком: на
        сервер она не уходит вовсе (`lib/pickup-selection.ts`). Стоит здесь,
        после «найти», потому что порядок полевой: нашёл — решил, берёшь ли.
      */}
      {inMyPool ? null : (
        <section className="mb-4">
          <BigButton
            tone={pickedUp ? 'primary' : 'neutral'}
            onClick={() => {
              togglePickup(bike.vehicleId)
            }}
          >
            {pickedUp ? 'Убрать из списка' : 'Забираю'}
          </BigButton>
        </section>
      )}

      {bike.task ? (
        <BikeTask
          task={bike.task}
          hubs={hubs.data}
          me={me}
          busy={complete.isPending}
          onComplete={() => {
            const completion = completionFor(bike.task!, me)
            if (!completion) return
            complete.mutate(
              { taskId: bike.task!.id, completion },
              { onSuccess: () => void navigate('/tasks') },
            )
          }}
        />
      ) : (
        <section className="mb-4">
          {/*
            ⚠ ПОРЯДОК ВЕТОК ЗНАЧИМ. `queuedCreate` проверяется ПЕРЕД
            `hasAnyTask`: тот считает и очередь, поэтому становится истинным
            в тот же тик, что и нажатие. Проверь его первым — и вместо
            «задача создана» человек увидит «задача уже есть», то есть
            новость о чужой работе вместо своей.
          */}
          {queuedCreate === 'rebalance' ? (
            <Notice tone="ok">
              Задача создана, «Выполнено» появится, как только уйдёт на сервер.
            </Notice>
          ) : queuedCreate === 'pickup' ? (
            <Notice tone="ok">
              Задача взята — она в разделе «Задания». Уйдёт сразу, а без сети — как
              появится связь.
            </Notice>
          ) : hasAnyTask ? (
            <Notice tone="info">На этот байк задача уже есть — она видна в заданиях.</Notice>
          ) : isPartner ? (
            <>
              <BigButton
                tone="primary"
                busy={createRebalance.isPending}
                disabled={bike.status !== 'available'}
                onClick={() => {
                  createRebalance.mutate({ vehicleId: bike.vehicleId, note: '' })
                }}
              >
                Переставить
              </BigButton>
              {bike.status !== 'available' ? (
                <p className="mt-1 text-xs opacity-50">
                  Переставлять можно только рабочий байк.
                </p>
              ) : null}
            </>
          ) : (
            <BigButton
              tone="neutral"
              busy={createPickup.isPending}
              onClick={() => {
                createPickup.mutate({ vehicleId: bike.vehicleId, note: pickupNoteFor(status) })
              }}
            >
              Забрать
            </BigButton>
          )}

          {createPickup.isError || createRebalance.isError ? (
            <div className="mt-2">
              <Notice tone="error">
                Действие не сохранено на устройстве. Проверьте, что браузеру разрешено
                хранить данные, и повторите.
              </Notice>
            </div>
          ) : null}
        </section>
      )}

      <details className="mb-4 rounded-xl border border-white/10 p-3">
        <summary className="cursor-pointer text-sm font-medium uppercase tracking-wide opacity-50">
          История
        </summary>
        <div className="mt-3">
          {hasPosition(liveBike) && online ? (
            <Suspense fallback={<div className="h-44 animate-pulse rounded-xl bg-white/5" />}>
              <MiniMap view={liveBike} dropped={droppedPoint} trail={trail.data} />
            </Suspense>
          ) : null}

          {droppedPoint ? (
            <p className="mt-2 text-sm opacity-70">
              Последнюю поездку закончили здесь{droppedAgo ? `, ${droppedAgo}` : ''}
            </p>
          ) : (
            <p className="mt-2 text-sm opacity-50">На байке ещё не ездили или след не записался.</p>
          )}

          {roles.includes('staff') ? (
            <p className="mt-2 text-xs opacity-40">Журнал статусов — в админке диспетчера.</p>
          ) : null}
        </div>
      </details>

      {/*
        Ручка `POST /v1/vehicles/{id}/report` открыта любому авторизованному
        (`handler_ops.go`), но партнёру эта кнопка не показывается намеренно
        — его работа кончается тем, что байк снова стоит ровно, заводить
        наряды диспетчеру не его задача (то же решение, что и для статусов).
      */}
      {isPartner ? null : (
        <>
          <BigButton
            tone="neutral"
            onClick={() => {
              setReportOpen(true)
            }}
          >
            Замечание диспетчеру
          </BigButton>
          <ReportSheet
            vehicleId={bike.vehicleId}
            open={reportOpen}
            onClose={() => {
              setReportOpen(false)
            }}
          />
        </>
      )}
    </main>
  )
}
